import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { CategoryMark } from '@/components/CategoryMark';

interface PaymentData {
  id: string;
  amount: number;
  description: string;
  category_id: string;
  category_name: string;
  category_icon: string;
  paid_at: string;
  paid_by: string;
  paid_by_name: string;
  memo?: string;
  is_settled: boolean;
  is_instant_request: boolean;
  share_percentage?: number;
  shares?: { user_id: string; share_percentage: number; share_amount: number }[];
}

export default function PaymentFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const { user } = useAuthStore();
  const { categories, showToast } = useAppStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);

  // 編集可能かどうか（未精算かつ自分が支払ったもの）
  const canEdit = paymentData ? (!paymentData.is_settled && paymentData.paid_by === user?.id) : true;
  // 閲覧のみモード（精算済みまたは他人が支払ったもの）
  const isViewOnly = isEdit && !canEdit;

  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    categoryId: '',
    paidAt: new Date().toISOString().split('T')[0],
    memo: '',
    sharePercentage: 50,
    isInstantRequest: false,
    isImmediateSettlement: false,
  });

  useEffect(() => {
    if (isEdit && id) {
      loadPayment(id);
    }
  }, [id]);

  const loadPayment = async (paymentId: string) => {
    setIsLoading(true);
    try {
      const res = await api.getPayments({});
      const payment = res.payments.find((p: any) => p.id === paymentId);
      if (payment) {
        setPaymentData(payment);

        // 自分の負担割合を取得
        const myShare = payment.shares?.find((s: any) => s.user_id === user?.id);
        const sharePercentage = myShare?.share_percentage ?? 50;

        setFormData({
          amount: String(payment.amount),
          description: payment.description,
          categoryId: payment.category_id || '',
          paidAt: payment.paid_at,
          memo: payment.memo || '',
          sharePercentage: sharePercentage,
          isInstantRequest: payment.is_instant_request || false,
          isImmediateSettlement: false,
        });
      }
    } catch (error) {
      showToast('読み込みに失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.description || !formData.categoryId) {
      showToast('必須項目を入力してください', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        amount: parseInt(formData.amount),
        description: formData.description,
        categoryId: formData.categoryId,
        paidAt: formData.paidAt,
        memo: formData.memo || undefined,
        sharePercentage: formData.sharePercentage,
        isInstantRequest: formData.isImmediateSettlement ? false : formData.isInstantRequest,
        isImmediateSettlement: formData.isImmediateSettlement,
      };

      if (isEdit && id) {
        await api.updatePayment(id, data);
        showToast('更新しました', 'success');
      } else {
        await api.createPayment(data);
        showToast('登録しました', 'success');
      }
      navigate('/payments');
    } catch (error: any) {
      showToast(error.message || '保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // 閲覧のみモード（詳細画面）
  if (isViewOnly && paymentData) {
    const myShare = paymentData.shares?.find(s => s.user_id === user?.id);
    const category = categories.find(c => c.id === paymentData.category_id);

    return (
      <div className="pb-6">
        {/* ヘッダー */}
        <div className="page-topbar">
          <div className="page-topbar-inner">
            <button onClick={() => navigate(-1)} className="text-gray-600">
              ← 戻る
            </button>
            <h1 className="text-lg font-bold">支出の詳細</h1>
            <div className="w-12"></div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* 精算済みバッジ */}
          {paymentData.is_settled && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <span className="text-green-700 font-medium">✓ 精算済み</span>
            </div>
          )}

          {/* 金額 */}
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-500 mb-1">金額</p>
            <p className="text-3xl font-bold">{formatCurrency(paymentData.amount)}</p>
          </div>

          {/* 詳細情報 */}
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            <div className="flex justify-between p-4">
              <span className="text-gray-500">内容</span>
              <span className="font-medium">{paymentData.description}</span>
            </div>
            <div className="flex justify-between p-4">
              <span className="text-gray-500">カテゴリ</span>
              <span className="font-medium">
                {category?.icon} {category?.name || paymentData.category_name}
              </span>
            </div>
            <div className="flex justify-between p-4">
              <span className="text-gray-500">日付</span>
              <span className="font-medium">
                {new Date(paymentData.paid_at).toLocaleDateString('ja-JP')}
              </span>
            </div>
            <div className="flex justify-between p-4">
              <span className="text-gray-500">支払った人</span>
              <span className="font-medium">
                {paymentData.paid_by === user?.id ? 'わたし' : paymentData.paid_by_name || 'パートナー'}
              </span>
            </div>
            <div className="flex justify-between p-4">
              <span className="text-gray-500">自分の負担</span>
              <span className="font-medium">
                {myShare?.share_percentage ?? 50}% ({formatCurrency(myShare?.share_amount ?? 0)})
              </span>
            </div>
            {paymentData.is_instant_request && (
              <div className="flex justify-between p-4">
                <span className="text-gray-500">即時精算</span>
                <span className="font-medium text-amber-600">リクエスト済み</span>
              </div>
            )}
            {paymentData.memo && (
              <div className="p-4">
                <p className="text-gray-500 mb-1">メモ</p>
                <p className="text-gray-700">{paymentData.memo}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 編集・新規作成モード
  return (
    <div className="pb-6">
      {/* ヘッダー */}
      <div className="page-topbar">
        <div className="page-topbar-inner">
          <button onClick={() => navigate(-1)} className="text-gray-600">
            キャンセル
          </button>
          <h1 className="text-lg font-bold">
            {isEdit ? '支出を編集' : '支出を追加'}
          </h1>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="text-primary-500 font-semibold"
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* 金額 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            金額 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              ¥
            </span>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: e.target.value })
              }
              placeholder="0"
              className="input pl-8 text-2xl font-bold"
              inputMode="numeric"
            />
          </div>
        </div>

        {/* 内容 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            内容 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="例: スーパーで買い物"
            className="input"
            maxLength={100}
          />
        </div>

        {/* カテゴリ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            カテゴリ <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-4 gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() =>
                  setFormData({ ...formData, categoryId: category.id })
                }
                className={`p-3 rounded-lg border text-center ${formData.categoryId === category.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200'
                  }`}
              >
                <CategoryMark name={category.name} color={category.color} size="sm" />
                <div className="text-xs truncate">{category.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 日付 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            日付
          </label>
          <input
            type="date"
            value={formData.paidAt}
            onChange={(e) =>
              setFormData({ ...formData, paidAt: e.target.value })
            }
            className="input"
          />
        </div>

        {/* 負担割合 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            相手の負担割合: {100 - formData.sharePercentage}%
          </label>
          <div className="px-2">
            <input
              type="range"
              min="0"
              max="100"
              value={formData.sharePercentage}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sharePercentage: parseInt(e.target.value),
                })
              }
              className="slider"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>自分0%</span>
              <span>50%ずつ</span>
              <span>自分100%</span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            自分の負担: {formData.sharePercentage}% / 相手の負担:{' '}
            {100 - formData.sharePercentage}%
          </p>
        </div>

        {/* 即時精算（すぐに精算済みにする） */}
        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
          <div>
            <p className="font-medium text-green-800">即時精算</p>
            <p className="text-sm text-green-600">
              すぐに精算済みとして登録します（通知なし）
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isImmediateSettlement}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  isImmediateSettlement: e.target.checked,
                })
              }
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
          </label>
        </div>

        {/* メモ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            メモ（任意）
          </label>
          <textarea
            value={formData.memo}
            onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
            placeholder="メモがあれば入力"
            className="input h-24 resize-none"
            maxLength={500}
          />
        </div>
      </div>
    </div>
  );
}
